import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Download,
  Edit2,
  FilePlus2,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import type { Suite, SuiteListItem } from '@/lib/checkpoint/types';
import { checkpointApi } from '@/lib/checkpoint/api';
import { ParamForm } from './ParamForm';
import { YamlEditDrawer } from './YamlEditDrawer';

/**
 * 统一的套件抽屉 —— 收编了旧版 AddSuiteButton（仅选择 + 挂载）和
 * SuiteManagerDrawer（仅 CRUD）两个分散入口。一个面板里同时支持：
 *
 *   - 搜索 / 选择套件 → 填参数 → 挂载
 *   - 新建（YAML 编辑）/ 导入（带冲突策略）/ 编辑 / 导出 / 删除
 *
 * 列表里不再渲染 example 角标 —— 内置 example 套件概念已下线，
 * 所有套件等价、可删除。
 */

interface Props {
  open: boolean;
  /** 当前 logcat tab 关心的 App 包名；用于列表分组排序 + 自动填 `package` 参数。 */
  currentPackage?: string;
  /** 已挂载的 suiteId 集合 —— 列表行显示"重新挂载"角标。 */
  mountedSuiteIds: Set<string>;
  /** 挂载回调：CheckpointPanel.handleMount 会负责"同套件只有一张卡"的去重。 */
  onMount: (suite: Suite, params: Record<string, string | number | boolean>) => void;
  /** 关闭回调。 */
  onClose: () => void;
  /** CRUD 变更回调 —— CheckpointPanel 清 suiteCache，让已挂载卡片下次刷新拉最新 YAML。 */
  onChanged: () => void;
}

type Group = '当前 App' | '通用' | '其他 App';
type ConflictStrategy = 'skip' | 'overwrite' | 'rename';

interface GroupedItem {
  group: Group;
  items: SuiteListItem[];
}

export function SuiteDrawer({
  open,
  currentPackage,
  mountedSuiteIds,
  onMount,
  onClose,
  onChanged,
}: Props): JSX.Element | null {
  const [items, setItems] = useState<SuiteListItem[]>([]);
  const [keyword, setKeyword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictStrategy>('skip');
  const [importLog, setImportLog] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 选中详情
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [pickedSuite, setPickedSuite] = useState<Suite | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string | number | boolean>>({});
  const [paramErrors, setParamErrors] = useState<Record<string, string>>({});

  // YamlEditDrawer 子抽屉
  const [editId, setEditId] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const r = await checkpointApi.listSuites();
      setItems(r.suites);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  // 关闭抽屉时重置选中态，下次打开是干净的
  useEffect(() => {
    if (!open) {
      setPickedId(null);
      setPickedSuite(null);
      setParamValues({});
      setParamErrors({});
      setKeyword('');
      setImportLog([]);
    }
  }, [open]);

  const grouped = useMemo<GroupedItem[]>(() => {
    const lower = keyword.toLowerCase();
    const filt = items.filter(
      (s) =>
        !lower ||
        s.id.toLowerCase().includes(lower) ||
        s.name.toLowerCase().includes(lower),
    );
    const cur: SuiteListItem[] = [];
    const generic: SuiteListItem[] = [];
    const other: SuiteListItem[] = [];
    for (const s of filt) {
      const ap = s.appPackage || '*';
      if (currentPackage && ap === currentPackage) cur.push(s);
      else if (ap === '*' || !ap) generic.push(s);
      else other.push(s);
    }
    return [
      ...(cur.length > 0 ? [{ group: '当前 App' as Group, items: cur }] : []),
      ...(generic.length > 0 ? [{ group: '通用' as Group, items: generic }] : []),
      ...(other.length > 0 ? [{ group: '其他 App' as Group, items: other }] : []),
    ];
  }, [items, keyword, currentPackage]);

  const pick = async (s: SuiteListItem): Promise<void> => {
    setPickedId(s.id);
    setBusy(true);
    setError(null);
    try {
      const detail = await checkpointApi.getSuite(s.id);
      setPickedSuite(detail.suite);
      const defaults: Record<string, string | number | boolean> = {};
      for (const p of detail.suite.parameters ?? []) {
        if (p.default !== undefined) defaults[p.name] = p.default;
      }
      // 当前 App 包名优先注入 `package`（约定俗成）
      if (currentPackage && (detail.suite.parameters ?? []).some((p) => p.name === 'package')) {
        defaults.package = currentPackage;
      }
      setParamValues(defaults);
      setParamErrors({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleMount = (): void => {
    if (!pickedSuite) return;
    const errs: Record<string, string> = {};
    for (const p of pickedSuite.parameters ?? []) {
      const v = paramValues[p.name];
      if (p.required && (v === undefined || v === '' || v === null) && p.default === undefined) {
        errs[p.name] = '必填';
      }
    }
    if (Object.keys(errs).length > 0) {
      setParamErrors(errs);
      return;
    }
    onMount(pickedSuite, paramValues);
    onClose();
  };

  const handleDelete = async (id: string): Promise<void> => {
    // eslint-disable-next-line no-alert
    if (!confirm(`确认删除套件 ${id}？`)) return;
    try {
      await checkpointApi.deleteSuite(id);
      if (pickedId === id) {
        setPickedId(null);
        setPickedSuite(null);
      }
      onChanged();
      void refresh();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  const handleExport = (id: string): void => {
    const a = document.createElement('a');
    a.href = checkpointApi.exportUrl(id);
    a.download = `${id}.yaml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleImportFiles = async (files: FileList | File[]): Promise<void> => {
    const arr = Array.from(files);
    setImportLog([]);
    for (const f of arr) {
      try {
        const text = await f.text();
        const result = await checkpointApi.importYaml(text, conflict);
        setImportLog((prev) => [...prev, `${f.name} -> ${result.id} (${result.status})`]);
      } catch (e) {
        setImportLog((prev) => [
          ...prev,
          `${f.name} -> 失败 (${e instanceof Error ? e.message : String(e)})`,
        ]);
      }
    }
    onChanged();
    void refresh();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/40">
        <div className="flex h-full w-full max-w-4xl flex-col bg-bg-card shadow-2xl">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="text-sm font-semibold">挂载检查套件</h2>
            <button
              onClick={onClose}
              className="text-ink-muted hover:text-ink"
              title="关闭"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2 text-[11px]">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="pointer-events-none absolute left-2 top-2 h-3 w-3 text-ink-dim" />
              <input
                className="input w-full pl-7"
                placeholder="搜索套件名 / id"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <button
              className="btn"
              onClick={() => setCreating(true)}
              title="从空模板新建套件"
            >
              <FilePlus2 className="h-3 w-3" /> 新建
            </button>
            <button
              className="btn"
              onClick={() => fileInputRef.current?.click()}
              title="导入 yaml 文件"
            >
              <Upload className="h-3 w-3" /> 导入
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".yaml,.yml"
              multiple
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  void handleImportFiles(e.target.files);
                  e.target.value = '';
                }
              }}
            />
            <label className="inline-flex items-center gap-1 text-ink-muted">
              冲突
              <select
                className="select py-0.5"
                value={conflict}
                onChange={(e) => setConflict(e.target.value as ConflictStrategy)}
              >
                <option value="skip">skip</option>
                <option value="overwrite">overwrite</option>
                <option value="rename">rename</option>
              </select>
            </label>
            <span className="ml-auto text-ink-muted">{items.length} 项</span>
          </div>

          {error && (
            <div className="border-b border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-300">
              {error}
            </div>
          )}
          {importLog.length > 0 && (
            <div className="border-b border-line bg-bg-soft px-3 py-1.5 text-[11px] text-ink-muted">
              <p className="font-medium">导入结果：</p>
              <ul className="ml-2 list-disc">
                {importLog.map((l, i) => (
                  <li key={i}>{l}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Main: list + inspector */}
          <div className="grid flex-1 min-h-0 grid-cols-[minmax(0,1fr)_360px]">
            {/* List */}
            <div className="overflow-y-auto border-r border-line">
              {busy && items.length === 0 && (
                <div className="p-3 text-[11px] text-ink-muted">加载中…</div>
              )}
              {!busy && grouped.length === 0 && (
                <div className="p-3 text-[11px] text-ink-muted">
                  {items.length === 0
                    ? '还没有套件 —— 点[新建]从空模板开始，或[导入]载入 yaml。'
                    : '没有匹配的套件。'}
                </div>
              )}
              {grouped.map((g) => (
                <div key={g.group}>
                  <h3 className="sticky top-0 z-[1] bg-bg-card px-3 py-1 text-[11px] font-semibold text-ink-muted">
                    {g.group}
                  </h3>
                  <ul className="divide-y divide-line">
                    {g.items.map((s) => {
                      const mounted = mountedSuiteIds.has(s.id);
                      return (
                        <li
                          key={s.id}
                          className={clsx(
                            'flex items-center gap-2 px-3 py-2 text-[11px]',
                            pickedId === s.id
                              ? 'bg-accent/10'
                              : 'hover:bg-bg-soft',
                          )}
                        >
                          <button
                            type="button"
                            className="flex-1 min-w-0 text-left"
                            onClick={() => void pick(s)}
                            title={s.description}
                          >
                            <div className="truncate font-mono text-ink">{s.name}</div>
                            <div className="truncate text-ink-muted">
                              {s.id} · {s.ruleCount} 规则 · {s.appPackage}
                            </div>
                          </button>
                          {mounted && (
                            <span
                              className="rounded bg-amber-500/15 px-1 py-[1px] text-[10px] text-amber-300"
                              title="此套件已挂载；再次挂载会用最新参数重置"
                            >
                              重新挂载
                            </span>
                          )}
                          <button
                            className="btn-ghost px-1 py-0.5"
                            title="编辑 yaml"
                            onClick={() => setEditId(s.id)}
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                          <button
                            className="btn-ghost px-1 py-0.5"
                            title="导出 yaml"
                            onClick={() => handleExport(s.id)}
                          >
                            <Download className="h-3 w-3" />
                          </button>
                          <button
                            className="btn-ghost px-1 py-0.5 text-rose-400 hover:bg-rose-500/10"
                            title="删除"
                            onClick={() => void handleDelete(s.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            {/* Inspector */}
            <aside className="flex flex-col overflow-y-auto bg-bg-soft">
              {!pickedSuite && (
                <div className="p-4 text-[11px] text-ink-muted">
                  在左侧选一个套件查看详情 / 填参数 / 挂载。
                </div>
              )}
              {pickedSuite && (
                <div className="flex flex-1 flex-col gap-3 p-3">
                  <div className="rounded-md border border-line bg-bg-card px-2 py-1.5 text-[11px]">
                    <div className="font-mono text-ink">{pickedSuite.name}</div>
                    <div className="text-ink-muted">
                      {pickedSuite.id} · {pickedSuite.rules.length} 规则 ·{' '}
                      {pickedSuite.appPackage ?? '*'}
                    </div>
                    {pickedSuite.description && (
                      <p className="mt-1 whitespace-pre-wrap text-ink-muted">
                        {pickedSuite.description}
                      </p>
                    )}
                  </div>
                  <ParamForm
                    parameters={pickedSuite.parameters ?? []}
                    values={paramValues}
                    onChange={setParamValues}
                    errors={paramErrors}
                  />
                  <div className="mt-auto flex items-center justify-end gap-2 border-t border-line pt-2">
                    <button
                      className="btn-primary inline-flex items-center gap-1"
                      onClick={handleMount}
                    >
                      <Plus className="h-3 w-3" />
                      {mountedSuiteIds.has(pickedSuite.id) ? '重新挂载' : '挂载'}
                    </button>
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* 子抽屉：编辑现有 yaml */}
      {editId && (
        <YamlEditDrawer
          suiteId={editId}
          onClose={() => setEditId(undefined)}
          onSaved={() => {
            setEditId(undefined);
            onChanged();
            void refresh();
          }}
        />
      )}

      {/* 子抽屉：从空模板新建 */}
      {creating && (
        <YamlEditDrawer
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            onChanged();
            void refresh();
          }}
        />
      )}
    </>
  );
}
